import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
	PhotoIcon,
	DocumentTextIcon,
	CodeBracketIcon,
	MegaphoneIcon,
	PlusIcon,
	PencilIcon,
	TrashIcon,
	ArrowUpIcon,
	ArrowDownIcon,
	EyeIcon,
	EyeSlashIcon
} from '@heroicons/react/24/outline';

import { API_URL } from '../../config/api';

const HeroManagement = () => {
	const [heroes, setHeroes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [showModal, setShowModal] = useState(false);
	const [editingHero, setEditingHero] = useState(null);
	const [formData, setFormData] = useState({
		content_type: 'text',
		title: '',
		subtitle: '',
		image: null,
		image_alt: '',
		image_link: '',
		html_content: '',
		ad_banner: null,
		ad_alt: '',
		ad_link: '',
		ad_script: '',
		cta_primary_text: '',
		cta_primary_link: '',
		cta_secondary_text: '',
		cta_secondary_link: '',
		active: true,
		order: 0
	});
	const [formErrors, setFormErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [imagePreview, setImagePreview] = useState(null);
	const [adPreview, setAdPreview] = useState(null);

	useEffect(() => {
		loadHeroes();
	}, []);

	const loadHeroes = async () => {
		try {
			setLoading(true);
			const response = await axios.get(`${API_URL}/api/content/heroes/`);
			setHeroes(Array.isArray(response.data) ? response.data : (response.data.results || []));
			setError('');
		} catch (err) {
			setError('Error al cargar banners');
			console.error('Error cargando heroes:', err);
		} finally {
			setLoading(false);
		}
	};

	const handleCreate = () => {
		setEditingHero(null);
		setFormData({
			content_type: 'text',
			title: '',
			subtitle: '',
			image: null,
			image_alt: '',
			image_link: '',
			html_content: '',
			ad_banner: null,
			ad_alt: '',
			ad_link: '',
			ad_script: '',
			cta_primary_text: '',
			cta_primary_link: '',
			cta_secondary_text: '',
			cta_secondary_link: '',
			active: true,
			order: heroes.length
		});
		setImagePreview(null);
		setAdPreview(null);
		setFormErrors({});
		setShowModal(true);
	};

	const handleEdit = (hero) => {
		setEditingHero(hero);
		setFormData({
			content_type: hero.content_type || 'text',
			title: hero.title || '',
			subtitle: hero.subtitle || '',
			image: null,
			image_alt: hero.image_alt || '',
			image_link: hero.image_link || '',
			html_content: hero.html_content || '',
			ad_banner: null,
			ad_alt: hero.ad_alt || '',
			ad_link: hero.ad_link || '',
			ad_script: hero.ad_script || '',
			cta_primary_text: hero.cta_primary_text || '',
			cta_primary_link: hero.cta_primary_link || '',
			cta_secondary_text: hero.cta_secondary_text || '',
			cta_secondary_link: hero.cta_secondary_link || '',
			active: hero.active !== undefined ? hero.active : true,
			order: hero.order || 0
		});
		setImagePreview(hero.image_url || null);
		setAdPreview(hero.ad_banner_url || null);
		setFormErrors({});
		setShowModal(true);
	};

	const handleSave = async (e) => {
		e.preventDefault();
		setFormErrors({});
		setSaving(true);

		try {
			const formDataToSend = new FormData();
			
			Object.keys(formData).forEach(key => {
				if (key === 'image' || key === 'ad_banner') {
					// Solo agregar si hay un archivo nuevo seleccionado
					// Si no hay archivo nuevo y estamos editando, no enviar el campo
					// para que Django mantenga la imagen existente
					if (formData[key]) {
						formDataToSend.append(key, formData[key]);
					}
				} else if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
					formDataToSend.append(key, formData[key]);
				}
			});

			if (editingHero) {
				await axios.put(`${API_URL}/api/content/heroes/${editingHero.id}/`, formDataToSend, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			} else {
				await axios.post(`${API_URL}/api/content/heroes/`, formDataToSend, {
					headers: { 'Content-Type': 'multipart/form-data' }
				});
			}

			await loadHeroes();
			setShowModal(false);
		} catch (err) {
			if (err.response?.data) {
				setFormErrors(err.response.data);
			} else {
				setError('Error al guardar banner');
			}
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Estás seguro de eliminar este banner?')) return;

		try {
			await axios.delete(`${API_URL}/api/content/heroes/${id}/`);
			await loadHeroes();
		} catch (err) {
			setError('Error al eliminar banner');
		}
	};

	const handleToggleActive = async (hero) => {
		try {
			await axios.patch(`${API_URL}/api/content/heroes/${hero.id}/`, {
				active: !hero.active
			});
			await loadHeroes();
		} catch (err) {
			setError('Error al cambiar estado del banner');
		}
	};

	const handleOrderChange = async (hero, direction) => {
		const newOrder = direction === 'up' ? hero.order - 1 : hero.order + 1;
		try {
			await axios.patch(`${API_URL}/api/content/heroes/${hero.id}/`, {
				order: newOrder
			});
			await loadHeroes();
		} catch (err) {
			setError('Error al cambiar orden');
		}
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setFormData({ ...formData, image: file });
			setImagePreview(URL.createObjectURL(file));
		}
	};

	const handleAdBannerChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setFormData({ ...formData, ad_banner: file });
			setAdPreview(URL.createObjectURL(file));
		}
	};

	const getContentTypeIcon = (type) => {
		switch (type) {
			case 'text': return DocumentTextIcon;
			case 'image': return PhotoIcon;
			case 'html': return CodeBracketIcon;
			case 'ad': return MegaphoneIcon;
			default: return DocumentTextIcon;
		}
	};

	const getContentTypeLabel = (type) => {
		switch (type) {
			case 'text': return 'Texto Simple';
			case 'image': return 'Imagen';
			case 'html': return 'HTML Embebido';
			case 'ad': return 'Publicidad/Banner';
			default: return type;
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
					<div>
						<h1 className="text-3xl font-bold text-gray-900">Gestión de Banners</h1>
						<p className="mt-2 text-sm text-gray-600">
							Administra los banners y contenido del hero de la página principal
						</p>
					</div>
					<button
						onClick={handleCreate}
						className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-opacity"
						style={{ backgroundColor: '#12284C' }}
					>
						<PlusIcon className="h-5 w-5" />
						Crear Banner
					</button>
				</div>
			</div>

			{/* Error */}
			{error && (
				<div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
					<p className="text-red-800">{error}</p>
				</div>
			)}

			{/* Lista de Banners */}
			{loading ? (
				<div className="text-center py-12">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#12284C' }}></div>
					<p className="mt-4 text-gray-600">Cargando banners...</p>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow overflow-hidden">
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Orden
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Tipo
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Vista Previa
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Título
									</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
										Estado
									</th>
									<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
										Acciones
									</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{heroes.length === 0 ? (
									<tr>
										<td colSpan="6" className="px-6 py-8 text-center text-gray-500">
											No hay banners creados
										</td>
									</tr>
								) : (
									heroes.map((hero) => {
										const Icon = getContentTypeIcon(hero.content_type);
										return (
											<tr key={hero.id} className="hover:bg-gray-50">
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="flex items-center gap-2">
														<button
															onClick={() => handleOrderChange(hero, 'up')}
															className="p-1 hover:bg-gray-200 rounded"
															disabled={hero.order === 0}
														>
															<ArrowUpIcon className="h-4 w-4" />
														</button>
														<span className="text-sm font-medium text-gray-900">{hero.order}</span>
														<button
															onClick={() => handleOrderChange(hero, 'down')}
															className="p-1 hover:bg-gray-200 rounded"
														>
															<ArrowDownIcon className="h-4 w-4" />
														</button>
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="flex items-center space-x-2">
														<Icon className="h-5 w-5 text-gray-400" />
														<span className="text-sm text-gray-900">{getContentTypeLabel(hero.content_type)}</span>
													</div>
												</td>
												<td className="px-6 py-4">
													{hero.image_url ? (
														<img 
															src={hero.image_url} 
															alt={hero.image_alt || hero.title || 'Banner'} 
															className="h-16 w-auto object-contain rounded"
														/>
													) : hero.ad_banner_url ? (
														<img 
															src={hero.ad_banner_url} 
															alt={hero.ad_alt || 'Banner'} 
															className="h-16 w-auto object-contain rounded"
														/>
													) : hero.content_type === 'html' ? (
														<div className="text-xs text-gray-400">HTML</div>
													) : (
														<div className="text-xs text-gray-400">Sin imagen</div>
													)}
												</td>
												<td className="px-6 py-4">
													<div className="text-sm font-medium text-gray-900">{hero.title || 'Sin título'}</div>
													{hero.subtitle && (
														<div className="text-sm text-gray-500 truncate max-w-md">{hero.subtitle}</div>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													{hero.active ? (
														<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
															Activo
														</span>
													) : (
														<span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
															Inactivo
														</span>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
													<div className="flex items-center justify-end gap-2">
														<button
															onClick={() => handleToggleActive(hero)}
															className="p-2 hover:bg-gray-100 rounded"
															title={hero.active ? 'Desactivar' : 'Activar'}
														>
															{hero.active ? (
																<EyeSlashIcon className="h-5 w-5 text-gray-600" />
															) : (
																<EyeIcon className="h-5 w-5 text-gray-600" />
															)}
														</button>
														<button
															onClick={() => handleEdit(hero)}
															className="p-2 hover:bg-gray-100 rounded"
															title="Editar"
														>
															<PencilIcon className="h-5 w-5" style={{ color: '#12284C' }} />
														</button>
														<button
															onClick={() => handleDelete(hero.id)}
															className="p-2 hover:bg-red-50 rounded"
															title="Eliminar"
														>
															<TrashIcon className="h-5 w-5 text-red-600" />
														</button>
													</div>
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
				</div>
			)}

			{/* Modal Crear/Editar */}
			{showModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
						<div className="p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-2xl font-bold text-gray-900">
									{editingHero ? 'Editar Banner' : 'Crear Banner'}
								</h2>
								<button
									onClick={() => setShowModal(false)}
									className="text-gray-400 hover:text-gray-600"
								>
									<svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSave} className="space-y-6">
								{/* Tipo de Contenido */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Tipo de Contenido *
									</label>
									<select
										required
										value={formData.content_type}
										onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
										style={{ focusRingColor: '#12284C' }}
									>
										<option value="text">Texto Simple</option>
										<option value="image">Imagen</option>
										<option value="html">HTML Embebido</option>
										<option value="ad">Publicidad/Banner</option>
									</select>
								</div>

								{/* Campos según tipo */}
								{formData.content_type === 'text' && (
									<>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Título *
											</label>
											<input
												type="text"
												required
												value={formData.title}
												onChange={(e) => setFormData({ ...formData, title: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Subtítulo
											</label>
											<textarea
												value={formData.subtitle}
												onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
												rows={3}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
									</>
								)}

								{formData.content_type === 'image' && (
									<>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Imagen {!editingHero ? '*' : '(dejar vacío para mantener la actual)'}
											</label>
											<input
												type="file"
												accept="image/*"
												onChange={handleImageChange}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg"
												required={!editingHero}
											/>
											{imagePreview && (
												<div className="mt-2">
													<img src={imagePreview} alt="Preview" className="max-w-xs h-auto rounded-lg" />
													{editingHero && (
														<p className="text-xs text-gray-500 mt-1">
															{formData.image ? 'Nueva imagen seleccionada' : 'Imagen actual'}
														</p>
													)}
												</div>
											)}
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Texto Alternativo
											</label>
											<input
												type="text"
												value={formData.image_alt}
												onChange={(e) => setFormData({ ...formData, image_alt: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												URL de Destino
											</label>
											<input
												type="url"
												value={formData.image_link}
												onChange={(e) => setFormData({ ...formData, image_link: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
									</>
								)}

								{formData.content_type === 'html' && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Contenido HTML *
										</label>
										<textarea
											required
											value={formData.html_content}
											onChange={(e) => setFormData({ ...formData, html_content: e.target.value })}
											rows={10}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-offset-0"
											style={{ focusRingColor: '#12284C' }}
											placeholder="<div>...</div>"
										/>
									</div>
								)}

								{formData.content_type === 'ad' && (
									<>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Banner de Publicidad {!editingHero ? '*' : '(dejar vacío para mantener el actual)'}
											</label>
											<input
												type="file"
												accept="image/*"
												onChange={handleAdBannerChange}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg"
												required={!editingHero}
											/>
											{adPreview && (
												<div className="mt-2">
													<img src={adPreview} alt="Preview" className="max-w-xs h-auto rounded-lg" />
													{editingHero && (
														<p className="text-xs text-gray-500 mt-1">
															{formData.ad_banner ? 'Nuevo banner seleccionado' : 'Banner actual'}
														</p>
													)}
												</div>
											)}
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Texto Alternativo
											</label>
											<input
												type="text"
												value={formData.ad_alt}
												onChange={(e) => setFormData({ ...formData, ad_alt: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												URL de Destino
											</label>
											<input
												type="url"
												value={formData.ad_link}
												onChange={(e) => setFormData({ ...formData, ad_link: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Script de Publicidad
											</label>
											<textarea
												value={formData.ad_script}
												onChange={(e) => setFormData({ ...formData, ad_script: e.target.value })}
												rows={5}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
												placeholder="<!-- Google Ads, Facebook Pixel, etc. -->"
											/>
										</div>
									</>
								)}

								{/* CTAs (opcionales para todos) */}
								<div className="border-t pt-4">
									<h3 className="text-lg font-medium text-gray-900 mb-4">Botones de Acción (Opcional)</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Texto Botón Principal
											</label>
											<input
												type="text"
												value={formData.cta_primary_text}
												onChange={(e) => setFormData({ ...formData, cta_primary_text: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Link Botón Principal
											</label>
											<input
												type="text"
												value={formData.cta_primary_link}
												onChange={(e) => setFormData({ ...formData, cta_primary_link: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Texto Botón Secundario
											</label>
											<input
												type="text"
												value={formData.cta_secondary_text}
												onChange={(e) => setFormData({ ...formData, cta_secondary_text: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Link Botón Secundario
											</label>
											<input
												type="text"
												value={formData.cta_secondary_link}
												onChange={(e) => setFormData({ ...formData, cta_secondary_link: e.target.value })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
									</div>
								</div>

								{/* Configuración */}
								<div className="border-t pt-4">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Orden
											</label>
											<input
												type="number"
												value={formData.order}
												onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-0"
												style={{ focusRingColor: '#12284C' }}
											/>
										</div>
										<div>
											<label className="flex items-center space-x-2">
												<input
													type="checkbox"
													checked={formData.active}
													onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
													className="rounded border-gray-300"
													style={{ accentColor: '#12284C' }}
												/>
												<span className="text-sm font-medium text-gray-700">Activo</span>
											</label>
										</div>
									</div>
								</div>

								{/* Errores */}
								{Object.keys(formErrors).length > 0 && (
									<div className="bg-red-50 border border-red-200 rounded-lg p-4">
										{Object.entries(formErrors).map(([key, value]) => (
											<p key={key} className="text-sm text-red-800">
												{key}: {Array.isArray(value) ? value.join(', ') : value}
											</p>
										))}
									</div>
								)}

								{/* Botones */}
								<div className="flex justify-end gap-3 pt-4 border-t">
									<button
										type="button"
										onClick={() => setShowModal(false)}
										className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
									>
										Cancelar
									</button>
									<button
										type="submit"
										disabled={saving}
										className="px-4 py-2 rounded-lg text-white transition-opacity disabled:opacity-50"
										style={{ backgroundColor: '#12284C' }}
									>
										{saving ? 'Guardando...' : 'Guardar'}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default HeroManagement;

